import type { IShare, ISubscription } from '@wovin/core'
import type { IFolderGroup, IFolderGroupMember } from './agent/AgentFolderGroups'
import type { IRoster, IRosterSource } from './agent/AgentRoster'
import Dexie from 'dexie'

export type TAppAgentCryptoKeypairTypes = 'derivation' | 'signing' | 'encryption' | 'signing-ecdsa' | 'eddsa' | 'mnemonic' | 'trust'
export interface IAppAgentCryptoKeypair {
	ag: string // compound primary key with type - string hash of appAgent
	type: TAppAgentCryptoKeypairTypes // compound primary key with ag
	keys: Partial<CryptoKeyPair> | { secretKey: Uint8Array } | { encryptedMnemonic: Uint8Array }
}

export interface IAppAgentStorageGateways {
	ag: string
	gateways: string[]
}
export interface IAppAgentDirectoryHandles {
	ag: string
	directoryHandle: FileSystemDirectoryHandle
}
export class Note3_StateDB extends Dexie {
	shares!: Dexie.Table<IShare, string>
	subscriptions!: Dexie.Table<ISubscription, string>
	rosters!: Dexie.Table<IRoster, string>
	rosterSources!: Dexie.Table<IRosterSource, string>
	cryptokeys!: Dexie.Table<IAppAgentCryptoKeypair, [string, string]>
	gateways!: Dexie.Table<IAppAgentStorageGateways, string>
	directoryHandles!: Dexie.Table<IAppAgentDirectoryHandles, string>
	folderGroups!: Dexie.Table<IFolderGroup, string>
	folderGroupMembers!: Dexie.Table<IFolderGroupMember, [string, string]>
	// agents!: Dexie.Table<IEmailAddress, number>
	// config!: Dexie.Table<IPhoneNumber, number>

	constructor() {
		super('Note3_StateDB')

		this.version(13).stores({
			publications: 'id, name, createdAt, appAgent, encryptedFor, publishedBy, lastPush, latestLogTs, isDeleted',
			subscriptions: 'id, name, createdAt, lastPull, lastCID, appAgent, encryptedFor, isDeleted',
			cryptokeys: '[ag+type]',
		}).upgrade(async (tx) => {
			await tx.table('publications').toCollection().modify((pub) => {
				pub.lastCID = pub.lastCid
				pub.createdAt = pub.ts
				delete pub.ts
				delete pub.lastCid
			})
			await tx.table('subscriptions').toCollection().modify((sub) => {
				sub.lastCID = sub.lastCid
				sub.createdAt = new Date()
				delete sub.lastCid
			})
		})
		this.version(14).upgrade(async (tx) => {
			await tx.table('publications').toCollection().modify((pub) => {
				pub.lastPush = pub.lastPush || null
				delete pub.ts
			})
			await tx.table('subscriptions').toCollection().modify((sub) => {
				sub.lastPull = sub.lastPull || null
				delete sub.lastCid
			})
		})
		this.version(15).upgrade(async (tx) => {
			await tx.table('publications').toCollection().modify((pub) => {
				pub.rootBlockIDs = pub.rootBlockID ? [pub.rootBlockID] : null
				delete pub.rootBlockID
			})
		})

		this.version(16).upgrade(async (tx) => {
			await tx.table('publications').toCollection().modify((pub) => {
				delete pub.rootBlockIDs // replaced by selectors
			})
		})

		// v17 was a blurp
		this.version(18).stores({
			gateways: 'ag',
			directoryHandles: 'ag',
		})

		// Migrate publications to shares
		this.version(19).stores({
			shares: 'id, name, createdAt, appAgent, encryptedFor, publishedBy, lastPush, latestLogTs, isDeleted',
			publications: 'id, name, createdAt, appAgent, encryptedFor, publishedBy, lastPush, latestLogTs, isDeleted',
			subscriptions: 'id, name, createdAt, lastPull, lastCID, appAgent, encryptedFor, isDeleted',
			cryptokeys: '[ag+type]',
			gateways: 'ag',
			directoryHandles: 'ag',
		}).upgrade(async (tx) => {
			// Migrate publications to shares
			try {
				const publications = await tx.table('publications').toArray()
				for (const pub of publications) {
					await tx.table('shares').put(pub)
				}
				await tx.table('publications').clear()
			} catch (e) {
				// publications table might not exist in fresh installs
				console.log('No publications table to migrate:', e)
			}
		})

		// Add realtime field to subscriptions
		this.version(20).stores({
			shares: 'id, name, createdAt, appAgent, encryptedFor, publishedBy, lastPush, latestLogTs, isDeleted',
			subscriptions: 'id, name, createdAt, lastPull, lastCID, appAgent, encryptedFor, isDeleted, realtime',
			cryptokeys: '[ag+type]',
			gateways: 'ag',
			directoryHandles: 'ag',
		})

		// Add rosters + rosterSources tables (new tables, no upgrade() needed)
		this.version(21).stores({
			shares: 'id, name, createdAt, appAgent, encryptedFor, publishedBy, lastPush, latestLogTs, isDeleted',
			subscriptions: 'id, name, createdAt, lastPull, lastCID, appAgent, encryptedFor, isDeleted, realtime',
			cryptokeys: '[ag+type]',
			gateways: 'ag',
			directoryHandles: 'ag',
			rosters: 'id, name, createdAt, lastPush, isDeleted',
			rosterSources: 'id, kind, createdAt, lastPull',
		})

		// Add folderGroups + folderGroupMembers tables (new tables, no upgrade() needed)
		this.version(22).stores({
			shares: 'id, name, createdAt, appAgent, encryptedFor, publishedBy, lastPush, latestLogTs, isDeleted',
			subscriptions: 'id, name, createdAt, lastPull, lastCID, appAgent, encryptedFor, isDeleted, realtime',
			cryptokeys: '[ag+type]',
			gateways: 'ag',
			directoryHandles: 'ag',
			rosters: 'id, name, createdAt, lastPush, isDeleted',
			rosterSources: 'id, kind, createdAt, lastPull',
			folderGroups: 'id, name, createdAt, isDeleted',
			folderGroupMembers: '[folderID+memberID], folderID, memberID, memberType, addedAt',
		})
	}
}

export const stateDB = new Note3_StateDB()
// @ts-expect-error window.
if (typeof window != 'undefined') window.stateDB = stateDB
